import React, { useState, useRef } from "react";
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { Button } from "../../design/button";
import { Divider } from "../../design/divider";
import { FieldGroup, Fieldset, Label } from "../../design/fieldset";
import { Heading } from "../../design/heading";
import { Input } from "../../design/input";
import { Field } from "@headlessui/react";

import logo from "../../assets/logo.png";
import { Text } from "../../design/text";
import { Link } from "../../design/link";
import PhoneNumberInput from "../../components/PhoneNumberInput";

const PhoneLoginPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState("+1");
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const navigate = useNavigate();

  // Initialize reCAPTCHA verifier lazily when needed
  const getRecaptchaVerifier = () => {
    if (!recaptchaContainerRef.current) {
      throw new Error("reCAPTCHA container not found");
    }

    // Reuse existing verifier if available
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }

    // Clear the container before creating a new verifier
    recaptchaContainerRef.current.innerHTML = "";

    // Create new verifier
    recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
      size: "invisible",
      callback: () => {
        // reCAPTCHA solved - will proceed with phone auth
      },
      "expired-callback": () => {
        setError("reCAPTCHA expired. Please try again.");
        // Clear verifier on expiry so it can be recreated
        recaptchaVerifierRef.current = null;
      },
    });

    return recaptchaVerifierRef.current;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate phone number
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    if (digitsOnly.length < 10) {
      setError("Please enter a valid phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      const appVerifier = getRecaptchaVerifier();

      // Render the reCAPTCHA widget first (required for invisible reCAPTCHA)
      await appVerifier.render();

      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confirmation);
      setStep(2);
    } catch (err: any) {
      console.error("Error sending verification code:", err);

      if (err.code === "auth/invalid-phone-number") {
        setError("Invalid phone number. Please check and try again.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a moment before trying again.");
      } else if (err.code === "auth/captcha-check-failed") {
        setError("reCAPTCHA verification failed. Please refresh and try again.");
      } else if (err.code === "auth/invalid-app-credential") {
        setError("Authentication configuration error. Please ensure localhost is authorized in Firebase Console.");
      } else if (err.code === "auth/operation-not-allowed") {
        setError("Phone authentication is not enabled. Please contact support.");
      } else {
        setError("Failed to send verification code. Please try again.");
      }

      // Clear the verifier on error so it can be recreated on next attempt
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch {
          // Ignore errors when clearing
        }
        recaptchaVerifierRef.current = null;
      }
      // Also clear the container
      if (recaptchaContainerRef.current) {
        recaptchaContainerRef.current.innerHTML = "";
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!confirmationResult) {
      setError("Please request a verification code first.");
      return;
    }

    if (verificationCode.length !== 6) {
      setError("Please enter the complete 6-digit verification code.");
      return;
    }

    setIsSubmitting(true);
    try {
      const userCredential = await confirmationResult.confirm(verificationCode);

      // Get the user's custom claims to determine their role
      const tokenResult = await userCredential.user.getIdTokenResult();
      const claims = tokenResult.claims;

      // Check if user has any permissions (supervisor, parent, or admin)
      const supervisorIds = (claims.supervisorIds as string[]) || [];
      const parentIds = (claims.parentIds as string[]) || [];
      const writeAccountIds = (claims.writeAccountIds as string[]) || [];
      const readAccountIds = (claims.readAccountIds as string[]) || [];

      if (supervisorIds.length > 0 || parentIds.length > 0 || writeAccountIds.length > 0 || readAccountIds.length > 0) {
        // User has permissions - redirect to unified dashboard
        navigate("/dashboard");
      } else {
        // No permissions found - might be a new user
        setError("No account found with this phone number. Please contact your provider.");
      }
    } catch (err: any) {
      console.error("Error verifying code:", err);

      if (err.code === "auth/invalid-verification-code") {
        setError("Incorrect verification code. Please check and try again.");
      } else if (err.code === "auth/code-expired") {
        setError("Verification code expired. Please request a new one.");
      } else {
        setError("Failed to verify code. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setStep(1);
    setVerificationCode("");
    setConfirmationResult(null);
    setError(null);
  };

  const handleBackToPhone = () => {
    setStep(1);
    setVerificationCode("");
    setConfirmationResult(null);
    setError(null);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-100 to-blue-200 dark:from-zinc-900 dark:to-zinc-800">
      <form
        method="post"
        className="w-full max-w-lg p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900"
        onSubmit={step === 1 ? handleSendCode : handleVerifyCode}
      >
        <img src={logo} alt="Logo" className="w-24 h-24 mx-auto" />
        <Fieldset>
          <Heading className="text-2xl font-semibold text-center">
            {step === 1 ? "Sign in with Phone" : "Enter Verification Code"}
          </Heading>
          <Text className="mt-2 text-center text-zinc-600 dark:text-zinc-400">
            {step === 1
              ? "For monitors and supervisors"
              : `We've sent a 6-digit code to ${phoneNumber}`}
          </Text>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 my-4">
            <div
              className={`w-3 h-3 rounded-full ${
                step === 1 ? "bg-blue-600" : "bg-zinc-300 dark:bg-zinc-600"
              }`}
            />
            <div className="w-8 h-0.5 bg-zinc-300 dark:bg-zinc-600" />
            <div
              className={`w-3 h-3 rounded-full ${
                step === 2 ? "bg-blue-600" : "bg-zinc-300 dark:bg-zinc-600"
              }`}
            />
          </div>

          <Divider className="my-6" />

          {error && (
            <div className="p-3 mb-4 text-red-700 bg-red-100 rounded-lg dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          {step === 1 ? (
            <FieldGroup>
              <Field>
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneNumberInput
                  defaultValue={phoneNumber}
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Enter your phone number"
                />
              </Field>
            </FieldGroup>
          ) : (
            <FieldGroup>
              <Field>
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  aria-label="Verification Code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter 6-digit code"
                  className="text-2xl tracking-widest text-center"
                />
              </Field>
            </FieldGroup>
          )}

          <Divider className="my-6" />

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting
              ? step === 1
                ? "Sending..."
                : "Verifying..."
              : step === 1
              ? "Send Verification Code"
              : "Verify Code"}
          </Button>

          {step === 2 && (
            <div className="mt-4 space-y-2">
              <Button
                type="button"
                plain
                className="w-full"
                onClick={handleResendCode}
                disabled={isSubmitting}
              >
                Resend Code
              </Button>
              <Button
                type="button"
                plain
                className="w-full"
                onClick={handleBackToPhone}
                disabled={isSubmitting}
              >
                Use Different Number
              </Button>
            </div>
          )}

          <div className="my-6 text-center">
            <Text>
              Provider or admin?{" "}
              <Link href="/login" className="text-blue-600 hover:underline">
                Sign in with email
              </Link>
            </Text>
          </div>
        </Fieldset>

        {/* Hidden reCAPTCHA container */}
        <div ref={recaptchaContainerRef} id="recaptcha-container" />
      </form>
    </div>
  );
};

export default PhoneLoginPage;
